import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, HeartHandshake, CalendarCheck, Lock, Compass, Sparkles, HandHeart, Leaf, BookOpenCheck, ChevronDown } from "lucide-react";
import API from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { useContent } from "@/context/ContentProvider";

const DEFAULT_MARQUEE = [
  "Terapia Individual", "Talleres Presenciales", "Cursos Online", "Terapia de Pareja",
  "Orientación Psicológica", "Talleres Online", "Tutorías", "Terapia Familiar",
];

const VALUE_ICONS = [ShieldCheck, HandHeart, BookOpenCheck, Sparkles, Compass, Leaf, ShieldCheck];
const TRUST_ICONS = [ShieldCheck, CalendarCheck, Lock];

const Home = () => {
  const { t } = useContent();
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  useEffect(() => {
    API.get("/products?featured=true").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [location]);

  const bentoItems = t("bento.items", []);
  const marqueeItems = t("marquee.items", DEFAULT_MARQUEE);
  const team = t("about.team", []);
  const values = t("about.values", []);
  const trustItems = t("trust.items", []);

  return (
    <div className="overflow-x-clip" data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-[#4b3a2f]">
        <div
          className="pointer-events-none absolute -right-40 top-1/2 h-[130%] w-[60%] -translate-y-1/2 rounded-full opacity-[0.07]"
          style={{ background: "radial-gradient(circle, #c9a24b 0%, transparent 65%)" }}
        />
        <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-32 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:px-8 lg:pb-16 lg:pt-24">
          <motion.div style={{ y: textY }}>
            <FadeIn delay={0.15}>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#c9a24b]/40 px-4 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#c9a24b]" />
                <span className="font-mono-d text-[11px] uppercase tracking-[0.28em] text-[#cdb79e]">
                  {t("hero.badge", "Bienestar emocional integral")}
                </span>
              </span>
            </FadeIn>

            <h1 className="mt-8 font-serif-d text-[13.5vw] font-medium leading-[0.98] text-[#f2e9dd] sm:text-6xl lg:text-[5.2rem]">
              <MaskedLine delay={0.3}>{t("hero.title_line1", "Un espacio seguro")}</MaskedLine>
              <MaskedLine delay={0.42}>
                {t("hero.title_line2", "para ")}
                <em className="text-[#c9a24b]">{t("hero.title_line2_gold", "reconectar")}</em>
              </MaskedLine>
              <MaskedLine delay={0.54}>{t("hero.title_line3", "contigo mismo.")}</MaskedLine>
            </h1>

            <FadeIn delay={0.75}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-[#cdb79e] sm:text-lg">
                {t("hero.subtitle", "Terapias, cursos y talleres de salud mental atendidos por especialistas certificadas.")}
              </p>
            </FadeIn>

            <FadeIn delay={0.9}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to={t("hero.cta1_link", "/catalogo")}
                  data-testid="hero-cta-catalog"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#c9a24b] px-8 py-4 text-sm font-semibold text-[#4b3a2f] transition-all duration-300 hover:bg-[#b8913b] hover:shadow-[0_15px_40px_rgba(201,162,75,0.35)]"
                >
                  {t("hero.cta1_text", "Explorar catálogo")}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to={t("hero.cta2_link", "/catalogo?category=terapia_individual")}
                  data-testid="hero-cta-therapy"
                  className="inline-flex items-center gap-3 rounded-full border border-[#cdb79e]/40 px-8 py-4 text-sm font-medium text-[#f2e9dd] transition-colors duration-300 hover:border-[#c9a24b] hover:text-[#c9a24b]"
                >
                  {t("hero.cta2_text", "Agendar una consulta")}
                </Link>
              </div>
            </FadeIn>

            <FadeIn delay={1.05}>
              <div className="mt-14 flex flex-wrap gap-8 border-t border-[#c9a24b]/20 pt-8">
                {(t("hero.stats", [])).map((s) => (
                  <div key={s.label}>
                    <p className="font-serif-d text-3xl font-semibold text-[#c9a24b]">{s.n}</p>
                    <p className="mt-1 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#cdb79e]">{s.label}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto hidden w-full max-w-md lg:block"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-4 rounded-t-[999px] rounded-b-3xl border border-[#c9a24b]/35" />
              <div className="arch-frame overflow-hidden">
                <img
                  src={t("hero.image_url", "")}
                  alt={t("hero.tag_title", "Sesión de terapia")}
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="absolute -bottom-6 -left-10 flex items-center gap-4 rounded-2xl bg-[#f2e9dd] p-5 shadow-[0_20px_60px_rgba(44,32,24,0.35)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#4b3a2f] text-[#c9a24b]">
                  <HeartHandshake size={22} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#4b3a2f]">{t("hero.tag_title", "Especialistas certificadas")}</p>
                  <p className="text-xs text-[#6b5b52]">{t("hero.tag_text", "Atención humana y cálida")}</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            className="flex h-12 w-7 items-start justify-center rounded-full border border-[#cdb79e]/50 p-1.5"
          >
            <span className="h-2 w-1 rounded-full bg-[#c9a24b]" />
          </motion.div>
        </motion.div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <div className="marquee-paused overflow-hidden border-y border-[#4b3a2f]/10 bg-[#f2e9dd] py-5">
        <div className="animate-marquee flex w-max items-center gap-10 whitespace-nowrap">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="flex items-center gap-10">
              <span className="font-serif-d text-xl italic text-[#6b4e3d]">{item}</span>
              <span className="h-1.5 w-1.5 rounded-full bg-[#c9a24b]" />
            </span>
          ))}
        </div>
      </div>

      {/* ---------- CATEGORIES BENTO ---------- */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono-d text-[11px] uppercase tracking-[0.28em] text-[#a47b52]">{t("bento.eyebrow", "Nuestros servicios")}</p>
              <h2 className="mt-4 font-serif-d text-4xl font-semibold text-[#4b3a2f] sm:text-5xl">
                {t("bento.title_plain", "Elige tu camino ")}
                <em className="text-[#c9a24b]">{t("bento.title_gold", "hacia el bienestar")}</em>
              </h2>
            </div>
            <Link to="/catalogo" data-testid="home-view-all" className="gold-underline text-sm font-medium text-[#6b4e3d]">
              {t("bento.link_text", "Ver todo el catálogo")}
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
          {(bentoItems.length ? bentoItems : [{ title: "Catálogo", desc: "", image_url: "", link: "/catalogo" }]).map((b, i) => (
            <Reveal key={i} delay={i * 0.07} className={i % 4 === 0 || i % 4 === 3 ? "md:col-span-7" : "md:col-span-5"}>
              <Link
                to={b.link || "/catalogo"}
                data-testid={`home-category-${i}`}
                className="group relative block h-72 overflow-hidden rounded-2xl md:h-80"
              >
                {b.image_url && (
                  <img
                    src={b.image_url}
                    alt={b.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c2018]/85 via-[#2c2018]/20 to-transparent transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <h3 className="font-serif-d text-3xl font-semibold text-[#f2e9dd]">{b.title}</h3>
                  <p className="mt-1 text-sm text-[#cdb79e]">{b.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#c9a24b]">
                    Descubrir <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FEATURED PRODUCTS ---------- */}
      <section className="bg-[#f2e9dd] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.28em] text-[#a47b52]">{t("featured.eyebrow", "Lo más elegido")}</p>
            <h2 className="mt-4 font-serif-d text-4xl font-semibold text-[#4b3a2f] sm:text-5xl">
              {t("featured.title_plain", "Servicios ")}
              <em className="text-[#c9a24b]">{t("featured.title_gold", "destacados")}</em>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {products.slice(0, 6).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
          {products.length === 0 && (
            <p className="mt-10 text-center text-[#6b5b52]">Cargando servicios…</p>
          )}
        </div>
      </section>

      {/* ---------- SOBRE NOSOTROS ---------- */}
      <section id="nosotros" className="bg-[#f2e9dd] py-20 md:py-28" data-testid="about-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono-d text-center text-[11px] uppercase tracking-[0.28em] text-[#a47b52]">{t("about.eyebrow", "Sobre nosotros")}</p>
            <h2 className="mx-auto mt-4 max-w-3xl text-center font-serif-d text-4xl font-semibold leading-tight text-[#4b3a2f] sm:text-5xl">
              {t("about.title_plain", "Un equipo que cree en tu ")}
              <em className="text-[#c9a24b]">{t("about.title_gold", "capacidad de sanar")}</em>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-[#4b3a2f]/10 bg-white p-10">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
                  <Sparkles size={22} />
                </span>
                <h3 className="mt-6 font-serif-d text-3xl font-semibold text-[#4b3a2f]">{t("about.mission_title", "Misión")}</h3>
                <p className="mt-3 leading-relaxed text-[#4b3a2f]/85">{t("about.mission_text", "")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-3xl border border-[#4b3a2f]/10 bg-white p-10">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
                  <Compass size={22} />
                </span>
                <h3 className="mt-6 font-serif-d text-3xl font-semibold text-[#4b3a2f]">{t("about.vision_title", "Visión")}</h3>
                <p className="mt-3 leading-relaxed text-[#4b3a2f]/85">{t("about.vision_text", "")}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 rounded-3xl bg-[#4b3a2f] p-8">
              {values.map((label, i) => {
                const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
                return (
                  <span key={i} className="inline-flex items-center gap-2 rounded-full border border-[#c9a24b]/35 px-5 py-2.5 text-sm text-[#f2e9dd]">
                    <Icon size={15} className="text-[#c9a24b]" /> {label}
                  </span>
                );
              })}
            </div>
          </Reveal>

          <div className="mt-16 grid gap-8 md:grid-cols-2">
            {team.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <article className="flex h-full flex-col overflow-hidden rounded-3xl border border-[#4b3a2f]/10 bg-white" data-testid={`team-card-${i}`}>
                  <div className="relative h-72 overflow-hidden">
                    <img src={m.photo} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2c2018]/70 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-6">
                      <h3 className="font-serif-d text-3xl font-semibold text-[#f2e9dd]">{m.name}</h3>
                      <p className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-[#c9a24b]">{m.role}</p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <p className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#a47b52]">Semblanza</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#4b3a2f]/90">{m.bio}</p>
                    <p className="mt-5 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#a47b52]">Trayectoria</p>
                    <p className="mt-2 text-sm italic leading-relaxed text-[#6b5b52]">{m.trajectory}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- TRUST ---------- */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {trustItems.map((f, i) => {
            const Icon = TRUST_ICONS[i % TRUST_ICONS.length];
            return (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-[#4b3a2f]/10 bg-white p-8 transition-shadow duration-500 hover:shadow-[0_20px_50px_rgba(75,58,47,0.12)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
                    <Icon size={22} />
                  </span>
                  <h3 className="mt-6 font-serif-d text-2xl font-semibold text-[#4b3a2f]">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#6b5b52]">{f.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="bg-[#f2e9dd] py-20 md:py-28" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono-d text-center text-[11px] uppercase tracking-[0.28em] text-[#a47b52]">{t("faq.eyebrow", "Preguntas frecuentes")}</p>
            <h2 className="mt-4 text-center font-serif-d text-4xl font-semibold text-[#4b3a2f] sm:text-5xl">
              {t("faq.title_plain", "Todo lo que ")}
              <em className="text-[#c9a24b]">{t("faq.title_gold", "necesitas saber")}</em>
            </h2>
          </Reveal>
          <div className="mt-12 space-y-3">
            {(t("faq.items", [])).map((f, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <details className="group rounded-2xl border border-[#e8ded4] bg-white p-6" data-testid={`faq-item-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between font-serif-d text-xl font-semibold text-[#4b3a2f]">
                    {f.q}
                    <ChevronDown size={18} className="text-[#c9a24b] transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-[#6b5b52]">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
